import React, { useEffect, useRef } from "react";
import "./RichTextEditor.css";

/**
 * A small rich text field for reviewer feedback.
 *
 * Reviewers structure feedback as bullets and numbered points, and that
 * structure is the meaning — a flattened paragraph loses which point is which.
 * A plain <textarea> cannot hold it: bullets pasted from Word or Outlook are
 * list markup, not characters, so they vanish on paste.
 *
 * Built on contentEditable with document.execCommand rather than pulling in an
 * editor library. execCommand is deprecated, but every browser still
 * implements it, the alternative is a multi-hundred-kilobyte dependency in a
 * bundle already over 800 kB, and the formatting needed here is six commands
 * wide. If this ever grows to tables or links, that trade flips and it should
 * become a real editor.
 *
 * The value is HTML. It is sanitised on the server on the way in and on the
 * way out — what happens here is for the author's benefit, not a security
 * boundary, because anything in a browser can be bypassed.
 */

// Paste as plain text unless it carries list markup worth keeping. Pasting raw
// Word HTML brings a mass of <span style> and mso- rules that survive nowhere
// and render badly in email, so it is stripped down to the structure.
const PASTE_KEEP = /<(ul|ol|li|b|strong|i|em|u|br|p|div)\b/i;

const escapeHtml = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const RichTextEditor = ({ value, onChange, placeholder, id, disabled }) => {
  const ref = useRef(null);
  const lastValue = useRef(value);

  // Write into the DOM only when the value changed from OUTSIDE. Assigning
  // innerHTML on every render would move the caret to the start on each
  // keystroke, which makes the field unusable.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (value !== lastValue.current || el.innerHTML !== (value || "")) {
      if (document.activeElement !== el) {
        el.innerHTML = value || "";
        lastValue.current = value;
      }
    }
  }, [value]);

  const emit = () => {
    const html = ref.current?.innerHTML ?? "";
    lastValue.current = html;
    // An empty contentEditable reports "<br>" or "<div><br></div>"; treat
    // those as empty so "no feedback given" stays detectable downstream.
    const normalised = /^(<br\s*\/?>|<div>\s*<br\s*\/?>\s*<\/div>|\s)*$/i.test(html)
      ? ""
      : html;
    onChange(normalised);
  };

  const exec = (command) => {
    ref.current?.focus();
    document.execCommand(command, false, null);
    emit();
  };

  const handlePaste = (e) => {
    const html = e.clipboardData?.getData("text/html") || "";
    const text = e.clipboardData?.getData("text/plain") || "";
    e.preventDefault();
    if (html && PASTE_KEEP.test(html)) {
      // Keep the list structure, drop Word's styling noise. The server
      // sanitiser is what makes this safe; this only improves what is kept.
      const stripped = html
        .replace(/<!--[\s\S]*?-->/g, "")
        .replace(/<\/?(o:p|xml|style|meta|link)[^>]*>/gi, "")
        .replace(/\sstyle="[^"]*"/gi, "")
        .replace(/\sclass="[^"]*"/gi, "");
      document.execCommand("insertHTML", false, stripped);
    } else {
      // Plain text: keep the line breaks the author typed, and nothing else
      // that is not visible. Runs of spaces and tabs inside a line collapse to
      // one (the browser would show one anyway, but the extra characters were
      // reaching the stored HTML and surfacing in the PDF as wide gaps), and
      // a line's leading/trailing whitespace goes. Text is escaped before it
      // is handed to insertHTML: pasted "<" or "&" is content, not markup —
      // unescaped, a "< >" in the paste swallowed everything after it.
      const cleaned = text
        .replace(/\r\n?/g, "\n")
        .split("\n")
        .map((line) => line.replace(/[ \t\u00a0]+/g, " ").trim())
        .map(escapeHtml)
        .join("<br>");
      document.execCommand("insertHTML", false, cleaned);
    }
    emit();
  };

  const isEmpty = !value || !String(value).replace(/<[^>]*>/g, "").trim();

  return (
    <div className={`rte ${disabled ? "is-disabled" : ""}`}>
      <div className="rte-toolbar" role="toolbar" aria-label="Text formatting">
        {[
          { cmd: "bold", label: "B", title: "Bold", style: { fontWeight: 700 } },
          { cmd: "italic", label: "I", title: "Italic", style: { fontStyle: "italic" } },
          { cmd: "underline", label: "U", title: "Underline", style: { textDecoration: "underline" } },
        ].map((b) => (
          <button
            key={b.cmd}
            type="button"
            className="rte-btn"
            title={b.title}
            aria-label={b.title}
            style={b.style}
            disabled={disabled}
            // onMouseDown, not onClick: clicking a button blurs the editable
            // area first, which collapses the selection the command needs.
            onMouseDown={(e) => {
              e.preventDefault();
              exec(b.cmd);
            }}
          >
            {b.label}
          </button>
        ))}
        <span className="rte-sep" />
        <button
          type="button"
          className="rte-btn"
          title="Bulleted list"
          aria-label="Bulleted list"
          disabled={disabled}
          onMouseDown={(e) => {
            e.preventDefault();
            exec("insertUnorderedList");
          }}
        >
          • List
        </button>
        <button
          type="button"
          className="rte-btn"
          title="Numbered list"
          aria-label="Numbered list"
          disabled={disabled}
          onMouseDown={(e) => {
            e.preventDefault();
            exec("insertOrderedList");
          }}
        >
          1. List
        </button>
      </div>

      <div
        id={id}
        ref={ref}
        className="rte-area"
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        data-empty={isEmpty ? "true" : "false"}
        onInput={emit}
        onBlur={emit}
        onPaste={handlePaste}
      />
    </div>
  );
};

export default RichTextEditor;
